import { NON_LOCAL_VENDORS } from 'components/common/vendorConfig';
import { useAuthContext } from 'contexts/AuthContext';
import FHIRFetcher from 'data/FHIRFetcher';
import type { Bundle, Consent as FhirConsent } from 'fhir/r4';
import * as fhirpath from 'fhirpath';
import { get } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Label, List, Loader, Segment } from 'semantic-ui-react';
import type { SemanticCOLORS, SemanticICONS } from 'semantic-ui-react';
import useSWR from 'swr';
import type { ResourceEntry } from 'utils/bundleNarrowing';

import { parseFHIRPreference, parseFHIRlocation } from '../../../utils/FHIRUtils';

import Consent from './consent';
import Labs from './labs';
import MaternityHealth from './maternity';
import Observations from './observations';
import type {
  ProfileChildBaseProps,
  ProfileDataEitherShape,
  ProfileMutate,
  ProfileObservationEntry,
} from './types';
import Vitals from './vitals';

export const profileCodes = [
  {
    name: 'Qualification Status',
    code: 'QUALIFICATION-STATUS,ELIGIBILITY-FILE-STATUS,PLATFORM-ACTIVITY,INTERVENTION-SELECTED,FOOD-INSECURITY',
    icon: 'star' as SemanticICONS,
    color: 'green' as SemanticCOLORS,
  },
  {
    name: 'Eligibility Status',
    code: 'ELIGIBILITY-STATUS',
    icon: 'file image outline' as SemanticICONS,
    color: 'yellow' as SemanticCOLORS,
  },
  {
    name: 'Allergies',
    code: 'ALLERGIES',
    icon: 'bug' as SemanticICONS,
    color: 'red' as SemanticCOLORS,
  },
  {
    name: 'Food Avoidances',
    code: 'FOOD-AVOIDANCE',
    icon: 'food' as SemanticICONS,
    color: 'red' as SemanticCOLORS,
  },
  {
    name: 'Food Preferences',
    code: 'FOOD-PREFERENCES',
    icon: 'like' as SemanticICONS,
    color: 'pink' as SemanticCOLORS,
  },
  {
    name: 'Delivery Preferences',
    code: 'DELIVERY-PREFERENCES',
    icon: 'truck' as SemanticICONS,
    color: 'blue' as SemanticCOLORS,
  },
  {
    name: 'Communication Preferences',
    code: 'COMMUNICATION-PREFERENCES',
    icon: 'american sign language interpreting' as SemanticICONS,
    color: 'teal' as SemanticCOLORS,
  },
  {
    name: 'Referral Type',
    code: 'REFERRAL-TYPE,PAYMENT-DETAILS,PAYMENT-FAILED,SETUP-SUCCESS,SETUP-FAILED,SETUP-CANCELLED,CONSULT-OUTCOME',
    icon: 'group' as SemanticICONS,
    color: 'orange' as SemanticCOLORS,
  },
  {
    name: 'Education',
    code: 'EDUCATIONAL-MATERIAL-PREFERENCES,access-content',
    icon: 'book' as SemanticICONS,
    color: 'blue' as SemanticCOLORS,
  },
  {
    name: 'Other Preferences',
    code: 'OTHER-PREFERENCES',
    icon: 'setting' as SemanticICONS,
    color: 'yellow' as SemanticCOLORS,
  },
  {
    name: 'Resources',
    code: 'RESOURCES',
    icon: 'star' as SemanticICONS,
    color: 'green' as SemanticCOLORS,
  },
  {
    name: 'Diagnosis',
    code: 'DIAGNOSIS',
    icon: 'stethoscope' as SemanticICONS,
    color: 'black' as SemanticCOLORS,
  },
  {
    name: 'Medications',
    code: 'MEDICATION-PREFERENCES',
    icon: 'pills' as SemanticICONS,
    color: 'purple' as SemanticCOLORS,
  },
];

export const labCodes = [
  {
    name: 'Laboratory',
    code: 'laboratory,1742-6,1751-7,1920-8,1975-2,2028-9,2075-0,2085-9,2089-1,2093-3,2160-0,2345-7,2571-8,2823-3,2885-2,2951-2,3094-0,8462-4,15074-8,17861-6,59261-8,BLOOD_GLUCOSE_PERCENT_TIME_IN_RANGE,BLOOD_SUGAR_READING,EGFR,GLUCOSE,UACR',
    icon: 'flask' as SemanticICONS,
    color: 'black' as SemanticCOLORS,
  },
];

export const vitalCodes = [
  {
    name: 'Vitals',
    code: '8462-4,8480-6,29463-7,39156-5,55284-4,WEIGHT,8302-2', //Must not be space seperated, only comma
    icon: 'user doctor' as SemanticICONS,
    color: 'green' as SemanticCOLORS,
  },
];

export const maternityHealthCodes = [
  {
    name: 'Maternity Health',
    code: 'maternity-health,MATERNITY-HEALTH', //ingestion maternity health observations are in caps
    icon: 'hourglass' as SemanticICONS,
    color: 'pink' as SemanticCOLORS,
  },
];

export const consentCodes = [
  {
    name: 'Consent',
    code: 'CONSENT',
    icon: 'clipboard check' as SemanticICONS,
    color: 'black' as SemanticCOLORS,
  },
];

/**
 * Props for {@link Profile}, as passed by `components/patient/index.tsx`.
 *
 * `profileData` is the `/Observation/?patient=...` search bundle. It reaches
 * here through the parent's `useState({})`, so before that query's first
 * `onSuccess` it is the empty object, not `undefined` — hence `Partial`.
 *
 * `getDataByCode`'s `props.profileData?.entry ?? props.profileData` fallback
 * anticipates a bare entry array this parent never sends; the `??` arm is
 * unreachable from both call sites.
 */
type ProfileProps = ProfileChildBaseProps & {
  /** `/Observation/?patient=...`, or the parent's `{}` seed before it resolves. */
  profileData?: Partial<Bundle> | undefined;
  /** Revalidates the parent's profile observation query. */
  profileMutate?: ProfileMutate | undefined;
  /**
   * The parent's `/Observation/?...&code=CONSENT` bundle, read only by
   * `getConsentData`. Distinct from the same-named local `useSWR` result below,
   * which queries `/Consent/` and shadows this prop everywhere after it — the
   * render's `consentData` reads are the local, not this.
   */
  consentData?: Bundle | undefined;
  /** Revalidates the parent's consent query; forwarded to `Consent`. */
  consentDataMutate?: ProfileMutate | undefined;
  /** Forwarded to `Labs`, which uses it to jump to the surveys tab. */
  setActiveTab?: ((tab: string) => void) | undefined;
  /** Forwarded to `Labs`, which uses it to open a specific survey response. */
  updateQRID?: ((id: string, isNewResponse: boolean) => void) | undefined;
  /**
   * The member's delivery ZIP, used to key the `/Location` vendor lookup. Comes
   * from `FlatPatient['delivery_zip']`, which the parser leaves as the raw
   * `fhirpath.evaluate` array.
   */
  zipCode?: unknown[] | undefined;
  /*
   * SUSPECTED DEFECT — the seven below are passed by `patient/index.tsx` and read
   * by nothing here or in any child. `medicationsData` is the costly one: the meds
   * pane fetches its own, so the parent's query is threaded here and discarded.
   * Declared rather than dropped, because removing a prop changes the object React
   * renders with.
   */
  medicationsData?: unknown;
  medicationsMutate?: unknown;
  setProfileData?: unknown;
  activeTab?: unknown;
  setQRID?: unknown;
  carePlanData?: unknown;
  carePlanMutate?: unknown;
};

const Profile = React.memo((props: ProfileProps) => {
  //function Profile(props: any) {
  const authInfo = useAuthContext();
  const [localVendor, setLocalVendor] = useState<string[]>([]);

  const activeObservations = (val: string) => {
    const obsData = getDataByCode(val);
    // Not the `[] | undefined` this claimed: `getDataByCode` returns an array and
    // `.filter` narrows nothing, so the `!` on the `.length` read below was
    // asserting away an `undefined` this expression never produced.
    const filteredList: ProfileObservationEntry[] = obsData.filter((entry: ProfileObservationEntry) =>
      val
        .toLowerCase()
        .split(',')
        // `string | undefined`: `includes` compares by value, so a code-less entry
        // simply does not match rather than throwing.
        .includes(get(entry, 'resource.code.coding[0].code')?.toLowerCase() as string),
    );
    return filteredList.length > 0 ? true : false;
  };

  const getDataByCode = (code: string) => {
    //Filters observation data to matching profile codes
    //
    // SUSPECTED DEFECT — `data` is read as two incompatible shapes without ever
    // being discriminated. The `??` fallback yields `BundleEntry[]` when
    // `profileData.entry` is present and the `Partial<Bundle>` itself otherwise,
    // and the guard then tests `.length` (array) and `.entry` (bundle) on that
    // one value, after which `.filter` is called on it unconditionally. The
    // fallback arm therefore reaches `.filter` on a non-array whenever
    // `profileData` is the parent's `{}` seed with no `entry` — except that
    // `{}` fails both guard tests, so the branch is unreachable today and the
    // bug is latent. It becomes live the moment a caller passes a bundle-shaped
    // object carrying `length` but not `entry`. Left exactly as written: the
    // expression and both guard tests are unchanged, and `data` keeps its
    // inferred union rather than being narrowed to a branch the runtime does
    // not pick.
    // Cast, not narrowing: the guards below test `.length` and `.entry` on the SAME
    // value, which is the defect described above, so the type carries both shapes.
    // The cast has to wrap the whole `??` — casting only the fallback arm does not
    // type, because the `entry` arm is `BundleEntry[]`.
    const data = (props.profileData?.entry ?? props.profileData) as ProfileDataEitherShape | undefined;
    if (data?.length || data?.entry) {
      // Soft-deleted observations stay in the store as entered-in-error; hide them in the UI.
      const activeEntries = data?.filter(
        (entry: ProfileObservationEntry) => entry?.resource?.status !== 'entered-in-error',
      );
      let filteredData = activeEntries?.filter((entry: ProfileObservationEntry) =>
        code
          .toLowerCase()
          .split(',')
          .includes(
            fhirpath.evaluate(entry?.resource, 'code.coding.first().code').toString().toLowerCase(),
          ),
      );
      const filteredCategoryData = activeEntries?.filter((entry: ProfileObservationEntry) =>
        code
          .toLowerCase()
          .split(',')
          .includes(
            fhirpath
              .evaluate(entry?.resource, 'category.first().coding.first().code')
              .toString()
              .toLowerCase(),
          ),
      );
      //this will remove duplicate observations. Could happen for labs.
      filteredData = Array.from(new Set([...filteredData, ...filteredCategoryData]));
      if (code === 'EDUCATIONAL-MATERIAL-PREFERENCES') {
        const newFilteredData: typeof filteredData = [];
        for (let i = 0; i < filteredData.length; i++) {
          let bool = true;
          for (let j = 0; j < newFilteredData.length; j++) {
            // `Observation.valueString` is `0..1`; a preference observation without
            // one throws here today rather than being skipped as a non-duplicate.
            if (
              filteredData[i]!.resource.valueString!.trim() ===
              newFilteredData[j]!.resource.valueString!.trim()
            ) {
              bool = false;
              break;
            }
          }
          if (bool) newFilteredData.push(filteredData[i]!);
        }
        filteredData = newFilteredData;
      }

      return filteredData;
    } else {
      return [];
    }
  };

  const getConsentData = () => {
    let filteredData: ProfileObservationEntry[] = [];
    if (props?.consentData?.entry) {
      filteredData = (props?.consentData?.entry as ProfileObservationEntry[])?.filter((entry: ProfileObservationEntry) =>
        consentCodes[0]!.code
          .toLowerCase()
          .split(',')
          .includes(parseFHIRPreference(entry.resource).code.toLowerCase()),
      );
      if (props.sidebar) {
        filteredData = filteredData?.slice(0, 5);
      }
      return filteredData;
    } else {
      return [];
    }
  };
  const options = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
  };
  const { data: consentData } = useSWR(
    [
      `/Consent/?patient=${props.patientId}&identifier=web-survey-proxy,Member App&_sort=-date`,
      authInfo.token,
    ],
    FHIRFetcher<fhir4.Bundle>,
    options,
  );

  const locationOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
  };

  let key: string | null = null;

  if (props.zipCode && props.zipCode.length > 0) {
    // `zipCode` is `unknown[]` — the raw `fhirpath.evaluate` array. Interpolating
    // it comma-joins every zip on file into one query parameter.
    key = `/Location?_count=50&address-postalcode=${props.zipCode as unknown as string}`;
  }

  //fetch locations data
  const {
    data: locationsData,
    /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- the value is a `fhir-sdoh` export whose module ships no usable types */
    error: _locationsError,
    mutate: _mutate,
  } = useSWR(key ? [key, authInfo.token] : null, FHIRFetcher<fhir4.Bundle>, locationOptions);

  useEffect(() => {
    if (!key) {
      setLocalVendor([]);
      return;
    }

    if (locationsData && locationsData.entry && locationsData.entry.length) {
      setLocalVendor([]);
      const tempTableData: string[] = [];
      // Cast, not `bundleEntriesOf` — that helper filters at runtime. Upstream is a plain
      // `locationsData.entry.forEach(...)` under the same enclosing guard.
      (locationsData.entry as ResourceEntry<'Location'>[]).forEach((fhirLocation) => {
        const location = parseFHIRlocation(fhirLocation.resource);

        const locationVendor = location.vendorName.toUpperCase();
        if (!NON_LOCAL_VENDORS.includes(locationVendor)) {
          // let temp = NON_LOCAL_VENDORS.filter((vendor) => locationVendor.indexOf(vendor) !== -1);
          let flag = false;
          for (let counter = 0; counter < NON_LOCAL_VENDORS.length; counter++) {
            if (locationVendor.indexOf(NON_LOCAL_VENDORS[counter]!) !== -1) {
              flag = true;
            }
          }
          if (!flag) tempTableData.push(location.vendorName);
        }
      });

      setLocalVendor(tempTableData);
    }

  }, [locationsData, key]);

  const displayVendorLabel = (vendor: string) => {
    return (
      <Label color="teal" basic>
        <div style={{ display: 'flex' }}>
          <div>
            <b>{vendor}</b>
          </div>
        </div>
      </Label>
    );
  };
  return (
    <div
      style={{
        width: '100%',
        float: 'left',
        marginTop: '15px',
        marginBottom: '15px',
      }}
    >
      {(!props.profileData || !consentData) && (
        <Loader active inline="centered" content="Loading Profile Data..." />
      )}
      {
        <Segment>
          <h4>
            <List.Icon name="shop" color="blue" />
            Local Food Vendor:
          </h4>
          {localVendor.length > 0
            ? localVendor.map((vendor: string) => displayVendorLabel(vendor))
            : displayVendorLabel('No Local Vendor Found')}
        </Segment>
      }
      {consentData && (
        <List key="consent">
          <List.Item style={{ textAlign: 'left' }}>
            <Segment>
              <List.Content style={{ display: 'inline-block' }}>
                <h4>
                  <List.Icon name={consentCodes[0]!.icon} color={consentCodes[0]!.color} />
                  {consentCodes[0]!.name}: &nbsp;
                </h4>
                <Label color={consentData?.entry?.[0] ? 'teal' : 'grey'} basic>
                  <div style={{ display: 'flex' }}>
                    <div>
                      <div>
                        <b>
                          {consentData?.entry?.[0]
                            ? 'Digitally T&C and PP accepted'
                            : 'Digitally no T&C and PP accepted'}
                        </b>
                      </div>
                      {/* Upstream is `consentData?.entry?.[0]?.resource?.dateTime` — the FIRST
                          entry, whatever it is. `bundleResources` filters to Consent first, so
                          its `[0]` is a different element whenever entry[0] is not a Consent, and
                          it substitutes `[]` for a missing entry. The cast narrows the union for
                          the `dateTime` read and emits nothing. */}
                      <div>
                        {(consentData?.entry?.[0]?.resource as FhirConsent | undefined)?.dateTime}
                      </div>
                    </div>
                  </div>
                </Label>
                {/*
                  The `!` on `patientId` and `mutate`, here and at the four panels
                  below: both are optional here and required by every child. An absent
                  `patientId` reaches `softDeleteObservation`, which throws on a missing
                  Provenance subject — the assertions preserve that throw rather than
                  claiming the values are present. See the SUSPECTED DEFECT note at the
                  head of `vitals.tsx`.
                */}
                <Consent
                  style={{ margin: '5px' }}
                  code={consentCodes[0]!.code}
                  patientId={props.patientId!}
                  data={getConsentData()}
                  mutate={props.profileMutate}
                  sidebar={props.sidebar}
                  org={props.org}
                  consentDataMutate={props.consentDataMutate!}
                />
              </List.Content>
            </Segment>
          </List.Item>
        </List>
      )}

      {props.profileData &&
        maternityHealthCodes.map((m) => activeObservations(m.code))[0] &&
        maternityHealthCodes.map((profile) => (
          <List key={profile.code}>
            <List.Item style={{ textAlign: 'left' }}>
              <Segment>
                <List.Content style={{ display: 'inline-block' }}>
                  <h4>
                    <List.Icon name={profile.icon} color={profile.color} />
                    {profile.name}: &nbsp;
                  </h4>
                  <MaternityHealth
                    style={{ margin: '5px' }}
                    maternityData={getDataByCode(profile.code)}
                    patientId={props.patientId!}
                    org={props.org}
                    sidebar={props.sidebar}
                    mutate={props.profileMutate!}
                  />
                </List.Content>
              </Segment>
            </List.Item>
          </List>
        ))}

      {props.profileData &&
        profileCodes.map((profile) => (
          <List key={profile.code}>
            <List.Item style={{ textAlign: 'left' }}>
              <Segment>
                <List.Content style={{ display: 'inline-block' }}>
                  <h4>
                    <List.Icon name={profile.icon} color={profile.color} />
                    {profile.name}: &nbsp;
                  </h4>
                  <Observations
                    style={{ margin: '5px' }}
                    code={profile.code}
                    patientId={props.patientId!}
                    data={getDataByCode(profile.code)}
                    mutate={props.profileMutate!}
                    sidebar={props.sidebar}
                    org={props.org}
                  />
                </List.Content>
              </Segment>
            </List.Item>
          </List>
        ))}
      {props.profileData &&
        labCodes.map((profile) => (
          <List key={profile.code}>
            <List.Item style={{ textAlign: 'left' }}>
              <Segment>
                <List.Content style={{ display: 'inline-block' }}>
                  <h4>
                    <List.Icon name={profile.icon} color={profile.color} />
                    {profile.name}: &nbsp;
                  </h4>
                  <Labs
                    style={{ margin: '5px' }}
                    code={profile.code}
                    patientId={props.patientId!}
                    org={props.org}
                    data={getDataByCode(profile.code)}
                    mutate={props.profileMutate!}
                    sidebar={props.sidebar}
                    setActiveTab={props.setActiveTab}
                    updateQRID={props.updateQRID}
                  />
                </List.Content>
              </Segment>
            </List.Item>
          </List>
        ))}
      {props.profileData &&
        vitalCodes.map((profile) => (
          <List key={profile.code}>
            <List.Item style={{ textAlign: 'left' }}>
              <Segment>
                <List.Content style={{ display: 'inline-block' }}>
                  <h4>
                    <List.Icon name={profile.icon} color={profile.color} />
                    {profile.name}: &nbsp;
                  </h4>
                  <Vitals
                    style={{ margin: '5px' }}
                    vitalsData={getDataByCode(profile.code)}
                    patientId={props.patientId!}
                    org={props.org}
                    sidebar={props.sidebar}
                    mutate={props.profileMutate!}
                  />
                </List.Content>
              </Segment>
            </List.Item>
          </List>
        ))}
    </div>
  );
});

export default Profile;
